import { useState, useEffect } from 'react'
import { reportesAPI, fmt, fmtFecha } from '../services/api'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts'
import * as XLSX from 'xlsx'

const hoy = () => new Date().toISOString().split('T')[0]
const diasAtras = (n) => {
  const d = new Date()
  d.setDate(d.getDate() - n)
  return d.toISOString().split('T')[0]
}
const inicioMes = () => {
  const d = new Date()
  d.setDate(1)
  return d.toISOString().split('T')[0]
}

// Formatea una cantidad vendida según cómo se vende el producto.
// Ojo: la base guarda `cantidad` como decimal de 3 posiciones (para poder
// vender 1.225 kg), así que Postgres devuelve el texto "14.000" para 14
// unidades. Imprimirlo tal cual se leía como catorce mil.
const fmtCantidad = (cantidad, unidad) => {
  const n = parseFloat(cantidad || 0)
  const esKilo = unidad === 'kg' || unidad === 'kilo'
  if (esKilo) {
    return `${n.toLocaleString('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 3 })} kg`
  }
  return `${Math.round(n).toLocaleString('es-CL')} un`
}

const PRESETS = [
  { key: 'hoy',    label: 'Hoy',           desde: hoy(),        hasta: hoy() },
  { key: 'ayer',   label: 'Ayer',          desde: diasAtras(1), hasta: diasAtras(1) },
  { key: 'semana', label: 'Últimos 7 días', desde: diasAtras(6), hasta: hoy() },
  { key: 'mes',    label: 'Este mes',      desde: inicioMes(),  hasta: hoy() },
]

export default function Reportes() {
  const [presetActivo, setPresetActivo] = useState('hoy')
  const [desde, setDesde] = useState(hoy())
  const [hasta, setHasta] = useState(hoy())

  const [resumen, setResumen]       = useState(null)
  const [topProd, setTopProd]       = useState([])
  const [porHora, setPorHora]       = useState([])
  const [porVendedor, setPorVendedor] = useState([])
  const [rangoDiario, setRangoDiario] = useState([])
  const [cargando, setCargando]     = useState(true)

  // Buscador "¿este producto se vendió?"
  const [busqueda, setBusqueda]       = useState('')
  const [resultados, setResultados]   = useState(null)
  const [buscando, setBuscando]       = useState(false)

  const esUnDia = desde === hasta

  useEffect(() => { cargar() }, [desde, hasta])


  const aplicarPreset = (p) => {
    setPresetActivo(p.key)
    setDesde(p.desde)
    setHasta(p.hasta)
  }

  const cargar = async () => {
    setCargando(true)
    try {
      if (esUnDia) {
        const [r1, r2, r3] = await Promise.all([
          reportesAPI.ventasDia({ fecha: desde }),
          reportesAPI.productosMasVendidos({ desde, hasta, limit: 8 }),
          reportesAPI.ventasPorVendedor({ desde, hasta }),
        ])
        setResumen(r1.data.resumen)
        setPorHora(r1.data.por_hora.map(h => ({
          hora: `${String(h.hora).padStart(2,'0')}:00`,
          ventas: parseInt(h.ventas || h.n_ventas || 0),
          monto: parseFloat(h.monto)
        })))
        setTopProd(r2.data)
        setPorVendedor(r3.data)
        setRangoDiario([])
      } else {
        const [r2, r3, r4] = await Promise.all([
          reportesAPI.productosMasVendidos({ desde, hasta, limit: 8 }),
          reportesAPI.ventasPorVendedor({ desde, hasta }),
          reportesAPI.ventasRango({ desde, hasta }),
        ])
        // Resumen agregado desde el rango diario
        const totalVentas = r4.data.reduce((s,d) => s + parseFloat(d.total_ventas||0), 0)
        const totalNeto   = r4.data.reduce((s,d) => s + parseFloat(d.total_neto||0), 0)
        const totalIva    = r4.data.reduce((s,d) => s + parseFloat(d.total_iva||0), 0)
        const nDocs       = r4.data.reduce((s,d) => s + parseInt(d.n_documentos||0), 0)
        setResumen({
          total_ventas: totalVentas, total_neto: totalNeto, total_iva: totalIva,
          n_documentos: nDocs, ticket_promedio: nDocs ? totalVentas/nDocs : 0,
          n_anulados: 0, monto_anulado: 0
        })
        setPorHora([])
        setTopProd(r2.data)
        setPorVendedor(r3.data)
        setRangoDiario(r4.data.map(d => ({ fecha: fmtFecha(d.fecha+'T12:00:00'), monto: parseFloat(d.total_ventas) })))
      }
    } finally { setCargando(false) }
  }

  const buscarProducto = async () => {
    const q = busqueda.trim()
    if (q.length < 2) return
    setBuscando(true)
    try {
      const { data } = await reportesAPI.buscarProducto({ q, desde, hasta })
      setResultados(data)
    } finally { setBuscando(false) }
  }

  const limpiarBusqueda = () => { setBusqueda(''); setResultados(null) }

  const exportarExcel = () => {
    const wb = XLSX.utils.book_new()

    // Hoja resumen
    const wsResumen = XLSX.utils.aoa_to_sheet([
      ['Reporte de Ventas RedCaja'],
      [`Período: ${fmtFecha(desde+'T12:00:00')} al ${fmtFecha(hasta+'T12:00:00')}`],
      [],
      ['Métrica', 'Valor'],
      ['Ventas totales', resumen?.total_ventas || 0],
      ['Neto', resumen?.total_neto || 0],
      ['IVA', resumen?.total_iva || 0],
      ['N° transacciones', resumen?.n_documentos || 0],
      ['Ticket promedio', Math.round(resumen?.ticket_promedio || 0)],
    ])
    XLSX.utils.book_append_sheet(wb, wsResumen, 'Resumen')

    // Hoja por vendedor
    const wsVendedor = XLSX.utils.aoa_to_sheet([
      ['Vendedor', 'N° Ventas', 'Boletas', 'Monto boletas', 'Tickets', 'Monto tickets', 'Total Vendido', 'Ticket Promedio', 'Anuladas'],
      ...porVendedor.map(v => [
        v.vendedor, v.n_ventas,
        v.n_boletas ?? 0, parseFloat(v.total_boletas || 0),
        v.n_tickets ?? 0, parseFloat(v.total_tickets || 0),
        parseFloat(v.total_ventas), Math.round(v.ticket_promedio), v.n_anulados
      ])
    ])
    XLSX.utils.book_append_sheet(wb, wsVendedor, 'Por Vendedor')

    // Hoja productos — la cantidad va como número puro y la unidad en su
    // propia columna, para que Excel pueda sumar y filtrar sin problemas.
    const wsProd = XLSX.utils.aoa_to_sheet([
      ['Producto', 'Código', 'Cantidad', 'Unidad', 'N° ventas', 'Monto total'],
      ...topProd.map(p => [
        p.nombre, p.codigo || '',
        parseFloat(p.unidades_vendidas),
        (p.unidad === 'kg' || p.unidad === 'kilo') ? 'kg' : 'un',
        p.n_ventas || 0,
        parseFloat(p.monto_total)
      ])
    ])
    XLSX.utils.book_append_sheet(wb, wsProd, 'Productos más vendidos')

    // Hoja diaria (si es rango)
    if (rangoDiario.length > 0) {
      const wsDiario = XLSX.utils.aoa_to_sheet([
        ['Fecha', 'Ventas'],
        ...rangoDiario.map(d => [d.fecha, d.monto])
      ])
      XLSX.utils.book_append_sheet(wb, wsDiario, 'Ventas por día')
    }

    XLSX.writeFile(wb, `reporte-redcaja-${desde}-a-${hasta}.xlsx`)
  }

  if (cargando) return <div className="page-loading"><div className="spinner"/><span>Cargando reportes...</span></div>

  return (
    <div style={{padding:24}}>
      <div className="flex-between mb-4">
        <div className="section-title" style={{marginBottom:0}}>Reportes</div>
        <button className="btn btn-secondary" onClick={exportarExcel}>
          📥 Exportar a Excel
        </button>
      </div>

      {/* Selector de rango */}
      <div className="card mb-4">
        <div style={{display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap'}}>
          {PRESETS.map(p => (
            <button key={p.key} onClick={()=>aplicarPreset(p)} style={{
              padding:'7px 14px', borderRadius:'6px', fontSize:'13px', fontWeight:'600',
              cursor:'pointer', border:'1px solid var(--color-border-md)',
              background: presetActivo===p.key ? 'var(--color-primary)' : 'var(--color-bg)',
              color: presetActivo===p.key ? 'white' : 'var(--color-text)',
            }}>{p.label}</button>
          ))}
          <div style={{display:'flex', alignItems:'center', gap:'8px', marginLeft:'8px'}}>
            <span style={{fontSize:'12px', color:'var(--color-muted)'}}>Desde</span>
            <input type="date" className="form-input" value={desde} max={hasta}
              onChange={e=>{ setDesde(e.target.value); setPresetActivo('custom') }} style={{width:150}}/>
            <span style={{fontSize:'12px', color:'var(--color-muted)'}}>Hasta</span>
            <input type="date" className="form-input" value={hasta} max={hoy()}
              onChange={e=>{ setHasta(e.target.value); setPresetActivo('custom') }} style={{width:150}}/>
          </div>
        </div>
      </div>

      {/* ═══ BUSCADOR: ¿este producto se vendió? ═══ */}
      <div className="card mb-4">
        <div className="section-title">¿Se vendió este producto?</div>
        <div style={{fontSize:12, color:'var(--color-muted)', marginBottom:10}}>
          Busca por nombre o código. Si no tuvo ventas en el período, igual aparece — con cero.
        </div>
        <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
          <input className="form-input" style={{flex:1, minWidth:200}}
            placeholder="Ej: palta, tomate, 7801234..."
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && buscarProducto()}/>
          <button className="btn btn-primary" onClick={buscarProducto}
            disabled={buscando || busqueda.trim().length < 2}>
            {buscando ? <span className="spinner"/> : '🔍 Buscar'}
          </button>
          {resultados && (
            <button className="btn btn-secondary" onClick={limpiarBusqueda}>Limpiar</button>
          )}
        </div>

        {resultados && (
          <div style={{marginTop:14}}>
            {resultados.length === 0 ? (
              <div className="empty-state" style={{padding:24}}>
                <p>No hay ningún producto con ese nombre o código en el catálogo</p>
              </div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Producto</th>
                      <th>Cantidad vendida</th>
                      <th>N° ventas</th>
                      <th>Monto</th>
                      <th>Última venta</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resultados.map(p => {
                      const vendio = parseFloat(p.unidades_vendidas || 0) > 0
                      return (
                        <tr key={p.id}>
                          <td style={{fontWeight:500}}>
                            {p.nombre}
                            {p.codigo && (
                              <div style={{fontSize:11, color:'var(--color-muted)'}}>{p.codigo}</div>
                            )}
                          </td>
                          <td style={{fontWeight:600, color: vendio ? 'var(--color-text)' : 'var(--color-muted)'}}>
                            {vendio ? fmtCantidad(p.unidades_vendidas, p.unidad) : 'Sin ventas'}
                          </td>
                          <td>{p.n_ventas || 0}</td>
                          <td style={{fontWeight:600, color: vendio ? 'var(--color-success)' : 'var(--color-muted)'}}>
                            {fmt(p.monto_total)}
                          </td>
                          <td style={{fontSize:12}}>
                            {vendio ? (
                              <span style={{color:'var(--color-muted)'}}>
                                {fmtFecha(p.ultima_venta_periodo + 'T12:00:00')}
                              </span>
                            ) : p.ultima_venta_global ? (
                              <span style={{color:'var(--color-danger)', fontWeight:500}}>
                                ⚠ Sin vender desde {fmtFecha(p.ultima_venta_global + 'T12:00:00')}
                              </span>
                            ) : (
                              <span style={{color:'var(--color-danger)', fontWeight:500}}>
                                ⚠ Nunca se ha vendido
                              </span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Métricas */}
      <div className="metric-grid mb-6">
        <div className="metric-card">
          <div className="metric-label">Ventas totales</div>
          <div className="metric-value" style={{color:'var(--color-primary)'}}>{fmt(resumen?.total_ventas)}</div>
          <div className="metric-sub">Neto: {fmt(resumen?.total_neto)} + IVA: {fmt(resumen?.total_iva)}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Transacciones</div>
          <div className="metric-value">{resumen?.n_documentos || 0}</div>
          {esUnDia && <div className="metric-sub">{resumen?.n_anulados || 0} anuladas</div>}
        </div>
        <div className="metric-card">
          <div className="metric-label">Ticket promedio</div>
          <div className="metric-value">{fmt(resumen?.ticket_promedio)}</div>
        </div>
        {esUnDia && (
          <div className="metric-card">
            <div className="metric-label">Monto anulado</div>
            <div className="metric-value" style={{color:'var(--color-danger)'}}>{fmt(resumen?.monto_anulado)}</div>
          </div>
        )}
      </div>

      <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:20}}>
        {/* Ventas por hora o por día */}
        <div className="card">
          <div className="section-title">{esUnDia ? 'Ventas por hora' : 'Ventas por día'}</div>
          {(esUnDia ? porHora : rangoDiario).length ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={esUnDia ? porHora : rangoDiario} margin={{top:0,right:0,bottom:0,left:0}}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)"/>
                <XAxis dataKey={esUnDia ? 'hora' : 'fecha'} tick={{fontSize:11,fill:'var(--color-muted)'}}/>
                <YAxis tick={{fontSize:11,fill:'var(--color-muted)'}} tickFormatter={v=>'$'+Math.round(v/1000)+'k'}/>
                <Tooltip formatter={v=>[fmt(v),'Monto']} labelStyle={{color:'var(--color-text)'}}
                  contentStyle={{background:'var(--color-surface)',border:'1px solid var(--color-border)',borderRadius:8,fontSize:12}}/>
                <Bar dataKey="monto" fill="var(--color-primary)" radius={[4,4,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="empty-state" style={{padding:40}}><p>Sin ventas en este período</p></div>
          )}
        </div>

        {/* Productos más vendidos */}
        <div className="card">
          <div className="section-title">Productos más vendidos</div>
          {topProd.length ? topProd.map((p, i) => (
            <div key={p.id} style={{display:'flex',alignItems:'center',gap:12,padding:'8px 0',borderBottom:'1px solid var(--color-border)'}}>
              <div style={{width:24,height:24,borderRadius:'50%',background:'var(--color-primary-bg)',color:'var(--color-primary)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700,flexShrink:0}}>
                {i+1}
              </div>
              <div style={{flex:1,fontSize:13}}>
                <div style={{fontWeight:500}}>{p.nombre}</div>
                <div style={{fontSize:11,color:'var(--color-muted)'}}>
                  {fmtCantidad(p.unidades_vendidas, p.unidad)}
                  {p.n_ventas ? ` · ${p.n_ventas} ${p.n_ventas === 1 ? 'venta' : 'ventas'}` : ''}
                </div>
              </div>
              <div style={{fontWeight:600,fontSize:13}}>{fmt(p.monto_total)}</div>
            </div>
          )) : (
            <div className="empty-state" style={{padding:40}}><p>Sin ventas aún</p></div>
          )}
        </div>
      </div>

      {/* Ventas por vendedor */}
      <div className="card mb-6">
        <div className="section-title">Ventas por vendedor</div>
        {porVendedor.length ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Vendedor</th>
                  <th>N° Ventas</th>
                  <th>Boletas</th>
                  <th>Tickets</th>
                  <th>Total vendido</th>
                  <th>Ticket promedio</th>
                  <th>Anuladas</th>
                </tr>
              </thead>
              <tbody>
                {porVendedor.map(v => (
                  <tr key={v.usuario_id}>
                    <td style={{fontWeight:500}}>{v.vendedor}</td>
                    <td>{v.n_ventas}</td>
                    <td>
                      {v.n_boletas ?? 0}
                      <span style={{color:'var(--color-muted)', fontSize:11, marginLeft:6}}>
                        {fmt(v.total_boletas || 0)}
                      </span>
                    </td>
                    <td>
                      {v.n_tickets ?? 0}
                      <span style={{color:'var(--color-muted)', fontSize:11, marginLeft:6}}>
                        {fmt(v.total_tickets || 0)}
                      </span>
                    </td>
                    <td style={{fontWeight:600, color:'var(--color-success)'}}>{fmt(v.total_ventas)}</td>
                    <td>{fmt(v.ticket_promedio)}</td>
                    <td style={{color: v.n_anulados > 0 ? 'var(--color-danger)' : 'var(--color-muted)'}}>{v.n_anulados}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state" style={{padding:30}}><p>Sin ventas en este período</p></div>
        )}
      </div>

      {/* Botón SII */}
      <div className="card" style={{background:'var(--color-primary-bg)',border:'1px solid #b5d4f4'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <div>
            <div style={{fontWeight:600,fontSize:14,color:'var(--color-primary)',marginBottom:4}}>Integración SII</div>
            <div style={{fontSize:13,color:'var(--color-muted)'}}>Conecta con el SII para emitir boletas y facturas electrónicas oficiales</div>
          </div>
          <button className="btn btn-primary" style={{flexShrink:0}}>Configurar SII</button>
        </div>
      </div>
    </div>
  )
}
